// API service for communicating with the MediDev .NET API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5022/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add Authorization header if token exists
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401 && !endpoint.includes('/auth/')) {
        // Try to refresh token
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry the original request with new token
          const newToken = localStorage.getItem('authToken');
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
            const retryResponse = await fetch(url, config);
            if (!retryResponse.ok) {
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              return await retryResponse.json();
            }
            return await retryResponse.text();
          }
        }

        // Token refresh failed - trigger logout
        this.handleAuthFailure();
        throw new Error('Authentication failed');
      }

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || `HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return await response.text();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication endpoints
  async login(credentials) {
    return await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async register(userData) {
    return await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async logout() {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        await this.request('/auth/logout', {
          method: 'POST',
        });
      } catch (error) {
        console.error('Logout API call failed:', error);
      } finally {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
    }
  }

  // Data endpoints
  async getCustomers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return await this.request(`/customers${query ? `?${query}` : ''}`);
  }

  async getCustomer(id) {
    return await this.request(`/customers/${id}`);
  }

  async createCustomer(customerData) {
    return await this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  }

  async updateCustomer(id, customerData) {
    return await this.request(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }

  async deleteCustomer(id) {
    return await this.request(`/customers/${id}`, {
      method: 'DELETE',
    });
  }

  async getPractitioners(params = {}) {
    const query = new URLSearchParams(params).toString();
    return await this.request(`/practitioners${query ? `?${query}` : ''}`);
  }

  async getPractitioner(id) {
    return await this.request(`/practitioners/${id}`);
  }

  async createPractitioner(practitionerData) {
    return await this.request('/practitioners', {
      method: 'POST',
      body: JSON.stringify(practitionerData),
    });
  }

  async updatePractitioner(id, practitionerData) {
    return await this.request(`/practitioners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(practitionerData),
    });
  }

  async deletePractitioner(id) {
    return await this.request(`/practitioners/${id}`, {
      method: 'DELETE',
    });
  }

  async assignPractitionerLocations(practitionerId, locationIds) {
    const locations = locationIds.map((locationId, index) => ({
      locationId,
      isPrimary: index === 0,
    }));
    return await this.request(`/practitioners/${practitionerId}/locations`, {
      method: 'POST',
      body: JSON.stringify({ locations }),
    });
  }

  async getServices(params = {}) {
    const query = new URLSearchParams(params).toString();
    return await this.request(`/services${query ? `?${query}` : ''}`);
  }

  async getService(id) {
    return await this.request(`/services/${id}`);
  }

  async createService(serviceData) {
    return await this.request('/services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async updateService(id, serviceData) {
    return await this.request(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  }

  async deleteService(id) {
    return await this.request(`/services/${id}`, {
      method: 'DELETE',
    });
  }

  async getAppointments(params = {}) {
    const query = new URLSearchParams(params).toString();
    return await this.request(`/appointments${query ? `?${query}` : ''}`);
  }

  async getOffices(params = {}) {
    const query = new URLSearchParams(params).toString();
    return await this.request(`/offices${query ? `?${query}` : ''}`);
  }

  async getOffice(id) {
    return await this.request(`/offices/${id}`);
  }

  async createOffice(officeData) {
    return await this.request('/offices', {
      method: 'POST',
      body: JSON.stringify(officeData),
    });
  }

  async updateOffice(id, officeData) {
    return await this.request(`/offices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(officeData),
    });
  }

  async deleteOffice(id) {
    return await this.request(`/offices/${id}`, {
      method: 'DELETE',
    });
  }

  async getCompanies(params = {}) {
    const query = new URLSearchParams(params).toString();
    return await this.request(`/companies${query ? `?${query}` : ''}`);
  }

  // User management endpoints
  async getUsers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return await this.request(`/users${query ? `?${query}` : ''}`);
  }

  async getUser(id) {
    return await this.request(`/users/${id}`);
  }

  async createUser(userData) {
    return await this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(id, userData) {
    return await this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(id) {
    return await this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Widget management endpoints
  async getWidgets(params = {}) {
    const query = new URLSearchParams(params).toString();
    return await this.request(`/bookingwidgets${query ? `?${query}` : ''}`);
  }

  async getWidget(id) {
    return await this.request(`/bookingwidgets/${id}`);
  }

  async createWidget(widgetData) {
    return await this.request('/bookingwidgets', {
      method: 'POST',
      body: JSON.stringify(widgetData),
    });
  }

  async updateWidget(id, widgetData) {
    return await this.request(`/bookingwidgets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(widgetData),
    });
  }

  async deleteWidget(id) {
    return await this.request(`/bookingwidgets/${id}`, {
      method: 'DELETE',
    });
  }

  async getWidgetAnalytics(id, from, to) {
    const params = new URLSearchParams({ from, to }).toString();
    return await this.request(`/bookingwidgets/${id}/analytics?${params}`);
  }

  async getWidgetThemes() {
    return await this.request('/bookingwidgets/themes');
  }

  async getWidgetEmbedCode(id) {
    return await this.request(`/bookingwidgets/${id}/embed-code`);
  }

  // Lightweight ping — any HTTP response means API is up; network error means down
  async checkHealth() {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      await fetch(`${this.baseURL}/auth/me`, {
        signal: controller.signal,
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
      clearTimeout(timeout);
      return true; // Got a response (even 401/500) — API is reachable
    } catch {
      clearTimeout(timeout);
      return false; // Network failure — API is down
    }
  }

  // Token refresh functionality
  async refreshToken() {
    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return false;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh(refreshToken);

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  async performTokenRefresh(refreshToken) {
    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.token) {
          localStorage.setItem('authToken', data.token);
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
          }
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // Handle authentication failures
  handleAuthFailure() {
    // Clear all auth data
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // Trigger a custom event that the AuthContext can listen to
    window.dispatchEvent(new CustomEvent('auth-failure'));

    // Redirect to login (fallback if event handler doesn't work)
    setTimeout(() => {
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }, 100);
  }
  // -- Schedule / Availability --------------------------------------------------

  async getWeeklySchedule(practitionerId) {
    return await this.request('/availability/practitioners/' + practitionerId + '/schedule');
  }

  async putDaySchedule(practitionerId, dayOfWeek, payload) {
    return await this.request(
      '/availability/practitioners/' + practitionerId + '/schedule/' + dayOfWeek,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
    );
  }

  async getScheduleOverrides(practitionerId, from, to) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to)   params.append('to', to);
    const qs = params.toString();
    return await this.request(
      '/availability/practitioners/' + practitionerId + '/schedule/overrides' + (qs ? '?' + qs : '')
    );
  }

  async addScheduleOverride(practitionerId, payload) {
    return await this.request(
      '/availability/practitioners/' + practitionerId + '/schedule/overrides',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
    );
  }

  // ── iCal URL integration (simple, no OAuth) ──────────────────────────────

  /** Get iCal connection for a practitioner (404 = not configured yet) */
  async getICalConnection(practitionerId) {
    return this.request(`/calendar/practitioners/${practitionerId}/ical`);
  }

  /** Save (create or update) iCal URL for a practitioner */
  async saveICalConnection(practitionerId, { icalUrl, calendarName }) {
    return this.request(`/calendar/practitioners/${practitionerId}/ical`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ icalUrl, calendarName })
    });
  }

  /** Trigger an immediate iCal sync */
  async syncICalNow(practitionerId) {
    return this.request(`/calendar/practitioners/${practitionerId}/ical/sync`, {
      method: 'POST'
    });
  }

  /** Remove iCal connection */
  async removeICalConnection(practitionerId) {
    return this.request(`/calendar/practitioners/${practitionerId}/ical`, {
      method: 'DELETE'
    });
  }


  // ── Calendar integration ──────────────────────────────────────────────────

  /** Get the Google OAuth authorization URL for a practitioner */
  async getGoogleCalendarAuthUrl(practitionerId) {
    return this.request(`/calendar/google/auth-url?practitionerId=${practitionerId}`);
  }

  /** Get calendar connection status for a practitioner */
  async getCalendarStatus(practitionerId, calendarType = 'google') {
    return this.request(`/calendar/practitioners/${practitionerId}/status/${calendarType}`);
  }

  /** Disconnect a practitioner's calendar */
  async disconnectCalendar(practitionerId, calendarType = 'google') {
    return this.request(
      `/calendar/practitioners/${practitionerId}/disconnect/${calendarType}`,
      { method: 'DELETE' }
    );
  }

  /** Manually trigger a calendar sync */
  async syncCalendar(practitionerId, calendarType = 'google') {
    return this.request(
      `/calendar/practitioners/${practitionerId}/sync`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          calendarType,
          startDate: new Date(Date.now() - 86400000).toISOString(),
          endDate:   new Date(Date.now() + 90 * 86400000).toISOString(),
          twoWaySync: false
        })
      }
    );
  }

  /** Get sync history for a practitioner */
  async getCalendarSyncHistory(practitionerId, limit = 10) {
    return this.request(`/calendar/practitioners/${practitionerId}/sync-history?limit=${limit}`);
  }

  /** Get all calendar events for a practitioner */
  async getCalendarEvents(practitionerId, calendarType, startDate, endDate) {
    const params = new URLSearchParams({
      calendarType,
      startDate: startDate.toISOString(),
      endDate:   endDate.toISOString()
    });
    return this.request(`/calendar/practitioners/${practitionerId}/events?${params}`);
  }

}
;

// Export singleton instance
export default new ApiService();
